import React, { useState, useEffect } from 'react';
import { Earth, Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import navData from '../data/nav.json';

interface NavItem {
  href: string;
  text: string;
}

export function SharedNavigation() {
  const { language, setLanguage, isRTL } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = navData[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href: string) => {
    if (href.startsWith('#')) {
      // If we're not on the home page, navigate there first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation to complete, then scroll
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 200); // Increased timeout to ensure page loads
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleNavClick = (item: NavItem) => {
    if (item.href === '/') {
      // Always scroll to top when clicking Home
      if (location.pathname === '/') {
        scrollToTop();
      } else {
        navigate('/');
        setTimeout(() => scrollToTop(), 100);
      }
    } else if (item.href.startsWith('#')) {
      scrollToSection(item.href);
    } else {
      // For other pages, navigate and scroll to top
      navigate(item.href);
      setTimeout(() => scrollToTop(), 100);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? `bg-black/10 backdrop-blur-md shadow-lg border-b border-white/20 ${isMobileMenuOpen ? 'pb-4' : ''}`
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => handleNavClick({ href: '/', text: 'Home' })}
              className="text-2xl font-bold text-white hover:text-blue-400 transition-colors"
            >
              {'{A}'}
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex items-baseline gap-4">
              {navItems.map(item => (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item)}
                  className="text-slate-300 hover:text-blue-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {item.text}
                </button>
              ))}
            </div>
          </div>

          {/* Language Controls */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors text-sm font-medium border border-white/20"
            >
              <Earth className="w-4 h-4" />
              <span>{language === 'en' ? 'AR' : 'EN'}</span>
            </button>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white/10 backdrop-blur-md rounded-lg mt-2 border border-white/20">
              {navItems.map(item => (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item)}
                  className="text-slate-300 hover:text-blue-400 block px-3 py-2 rounded-md text-base font-medium transition-colors w-full text-left"
                >
                  {item.text}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
